import React, { useEffect, useRef, useState } from "react";
import { getUserInfo } from "../../services/AuthService";
import {
  searchConversations,
  listProjects,
  createProject,
  deleteProject as apiDeleteProject,
  getProject,
  updateProject as apiUpdateProject,
} from "../../services/ChatHistoryService";
import { toast } from "react-toastify";
import ConfirmDialog from "../common/ConfirmDialog";
import CreateProjectModal from "./CreateProjectModal";
import EditProjectModal from "./EditProjectModal";
import ShareProjectModal from "./ShareProjectModal";

function SidebarNavbar({
  sidebarOpen,
  conversations,
  activeConversation,
  onNewChat,
  onSwitchConversation,
  onDeleteConversation,
  onTogglePinned,
  currentProjectId = "all",
  onChangeProject,
  onProjectPromptChange,
  refreshConversationList,
  onRequestClose,
}) {
  const userInfo = getUserInfo() || {};
  const displayName =
    userInfo.username ||
    userInfo.fullName ||
    userInfo.name ||
    userInfo.userName ||
    "Người dùng";
  const email = userInfo.email || userInfo.mail || "";
  const initial = (displayName || email || "U").charAt(0).toUpperCase();

  // ── Search state ─────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchDebounceRef = useRef(null);

  // ── Project state ────────────────────────────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [creatingProject, setCreatingProject] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);

  // Edit / Share project modal state
  const [editProject, setEditProject] = useState(null); // project đang sửa
  const [editingProject, setEditingProject] = useState(false);
  const [shareProjectItem, setShareProjectItem] = useState(null); // project đang chia sẻ
  const [openMenuId, setOpenMenuId] = useState(null); // project _id đang mở menu "..."
  const projectMenuRef = useRef(null);

  // ── Confirm dialogs state ────────────────────────────────────────────────
  const [confirmDeleteConv, setConfirmDeleteConv] = useState(null); // { id, title }
  const [deletingConv, setDeletingConv] = useState(false);
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(null); // { _id, name }
  const [deletingProject, setDeletingProject] = useState(false);

  const loadProjects = async () => {
    try {
      const res = await listProjects();
      const list = res?.data || [];
      setProjects(list);

      // Reconcile: nếu currentProjectId đang trỏ tới 1 project cụ thể mà user
      // KHÔNG có quyền (không nằm trong danh sách project trả về — ví dụ
      // projectId cũ còn sót trong localStorage từ tài khoản trước), thì reset
      // về "all" để tránh mọi request bị 403 (kể cả tạo chat mới).
      if (
        currentProjectId &&
        currentProjectId !== "all" &&
        currentProjectId !== "unassigned" &&
        !list.some((p) => String(p._id) === String(currentProjectId))
      ) {
        onChangeProject?.("all");
        onProjectPromptChange?.("");
      }
    } catch (e) {
      console.error("listProjects:", e);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (!v.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await searchConversations({ q: v, page: 1, limit: 30 });
        setSearchResults(res?.data || []);
      } catch (e) {
        console.error("search:", e);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setSearching(false);
  };

  const handleSwitch = (id) => {
    onSwitchConversation(id);
    clearSearch();
    if (window.innerWidth < 769 && onRequestClose) onRequestClose();
  };

  const handleNewChat = () => {
    onNewChat();
    if (window.innerWidth < 769 && onRequestClose) onRequestClose();
  };

  const requestDeleteConv = (conv) => {
    setConfirmDeleteConv(conv);
  };

  const handleConfirmDeleteConv = async () => {
    if (!confirmDeleteConv) return;
    setDeletingConv(true);
    try {
      // Parent đã wire DELETE API trong onDeleteConversation(id, event)
      await onDeleteConversation(confirmDeleteConv.id, { stopPropagation: () => { } });
      setConfirmDeleteConv(null);
    } catch (e) {
      console.error("delete conv:", e);
      toast.error("Xoá chat thất bại");
    } finally {
      setDeletingConv(false);
    }
  };

  const requestDeleteProject = (project, e) => {
    e.stopPropagation();
    setConfirmDeleteProject(project);
  };

  const handleConfirmDeleteProject = async () => {
    if (!confirmDeleteProject) return;
    setDeletingProject(true);
    try {
      await apiDeleteProject(confirmDeleteProject._id);
      await loadProjects();
      if (currentProjectId === String(confirmDeleteProject._id) && onChangeProject) {
        onChangeProject("all");
      }
      toast.success("Đã xoá project");
      setConfirmDeleteProject(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Xoá project thất bại");
    } finally {
      setDeletingProject(false);
    }
  };

  const pinnedConvs = conversations.filter((c) => c.pinned);
  const otherConvs = conversations.filter((c) => !c.pinned);

  const isSearchMode = !!searchQuery.trim();

  // ── Search box toggle (ChatGPT mở input khi bấm "Tìm kiếm") ────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  // ── Project section: hiển thị tối đa N, còn lại ẩn sau "Thêm" ─────────────
  const [showAllProjects, setShowAllProjects] = useState(false);
  // ── Đóng/mở toàn bộ list chat "Gần đây" (giống ChatGPT) ───────────────────
  const [recentCollapsed, setRecentCollapsed] = useState(false);
  // ── Đóng/mở toàn bộ danh sách dự án ───────────────────────────────────────
  const [projectsCollapsed, setProjectsCollapsed] = useState(false);
  const PROJECTS_PREVIEW = 5;
  const visibleProjects = showAllProjects
    ? projects
    : projects.slice(0, PROJECTS_PREVIEW);

  // ── Tạo dự án qua modal ────────────────────────────────────────────────────
  const handleCreateProjectSubmit = async (payload) => {
    const name = String(payload?.name || "").trim();
    if (!name) return;
    setCreatingProject(true);
    try {
      await createProject({ name, prompt: payload?.prompt || "" });
      await loadProjects();
      toast.success("Đã tạo dự án");
      setCreateProjectOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Không tạo được dự án");
    } finally {
      setCreatingProject(false);
    }
  };

  // ── Mở modal sửa dự án (fetch chi tiết để có prompt + role) ─────────────────
  const openEditProject = async (project) => {
    setOpenMenuId(null);
    try {
      const res = await getProject(project._id);
      setEditProject(res?.data || project);
    } catch {
      setEditProject(project);
    }
  };

  const handleSaveProject = async ({ name, prompt }) => {
    if (!editProject?._id) return;
    setEditingProject(true);
    try {
      await apiUpdateProject(editProject._id, { name, prompt });
      await loadProjects();
      // Nếu đang ở trong project này → cập nhật prompt đang dùng ngay.
      if (
        String(currentProjectId) === String(editProject._id) &&
        typeof onProjectPromptChange === "function"
      ) {
        onProjectPromptChange(prompt || "");
      }
      toast.success("Đã cập nhật dự án");
      setEditProject(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Không cập nhật được dự án");
    } finally {
      setEditingProject(false);
    }
  };

  // Đóng menu "..." khi click ra ngoài
  useEffect(() => {
    if (!openMenuId) return;
    const onClick = (e) => {
      if (projectMenuRef.current && !projectMenuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [openMenuId]);

  return (
    <aside
      className={`pim-sidebar pim-sidebar-scroll h-full min-h-0 shrink-0 flex flex-col ${sidebarOpen ? "" : "pim-sidebar-hidden"
        }`}
      aria-label="Lịch sử chat"
    >
      {/* Header: brand + collapse */}
      <div className="px-3 pt-3 pb-1">
        <div className="flex items-center justify-between h-9 px-1">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center text-white text-[13px] font-bold shadow-sm">
              π
            </div>
            <span className="font-semibold text-gray-900 text-[15px] truncate">
              PIMATH
            </span>
          </div>
          {onRequestClose && (
            <button
              type="button"
              onClick={onRequestClose}
              className="pim-side-row !w-auto !p-1.5"
              title="Đóng sidebar"
              aria-label="Đóng sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 4v16M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Top actions: New chat + Search */}
      <div className="px-3 pt-1">
        <button type="button" onClick={handleNewChat} className="pim-side-row">
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zM19.5 14.25v4.5A2.25 2.25 0 0117.25 21H5.25A2.25 2.25 0 013 18.75V6.75A2.25 2.25 0 015.25 4.5h4.5" />
          </svg>
          Đoạn chat mới
        </button>

        {!searchOpen ? (
          <button type="button" onClick={openSearch} className="pim-side-row">
            <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Tìm kiếm đoạn chat
          </button>
        ) : (
          <div className="relative px-1 py-1">
            <svg
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onBlur={() => {
                if (!searchQuery.trim()) setSearchOpen(false);
              }}
              placeholder="Tìm kiếm đoạn chat..."
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-400 bg-white"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  clearSearch();
                  setSearchOpen(false);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700"
                title="Xoá tìm kiếm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Scrollable: Projects + Recent */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-2 pt-1">
        {isSearchMode ? (
          // Search results
          <div>
            <p className="pim-side-label">
              {searching ? "Đang tìm…" : `Kết quả: ${searchResults.length}`}
            </p>
            {!searching && searchResults.length === 0 && (
              <p className="px-2 py-3 text-sm text-gray-400 italic">
                Không tìm thấy kết quả.
              </p>
            )}
            {searchResults.map((r) => (
              <div
                key={r.conversationId + (r.matchedMessageId || "")}
                onClick={() => handleSwitch(r.conversationId)}
                className="pim-conv-item flex-col items-start gap-1"
                style={{ alignItems: "stretch" }}
              >
                <div className="flex items-center gap-2 w-full">
                  <span className="truncate flex-1 text-sm">{r.title}</span>
                </div>
                {r.snippet && (
                  <div className="text-[12px] text-gray-500 line-clamp-2">
                    {r.snippet}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* ── Dự án (đóng/mở toàn bộ danh sách) ── */}
            <button
              type="button"
              onClick={() => setProjectsCollapsed((v) => !v)}
              className="pim-side-label mt-1 w-full flex items-center justify-between gap-2 hover:bg-[#ececec] rounded-lg transition-colors"
              aria-expanded={!projectsCollapsed}
            >
              <span>Dự án</span>
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${projectsCollapsed ? "-rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {!projectsCollapsed && (
              <>
                <button
                  type="button"
                  onClick={() => setCreateProjectOpen(true)}
                  className="pim-side-row"
                  disabled={creatingProject}
                >
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 11v4m2-2h-4" />
                  </svg>
                  Dự án mới
                </button>

                {visibleProjects.map((p) => {
                  const role = p.role || "viewer";
                  const canEdit = role === "owner" || role === "editor";
                  const canShare = role === "owner";
                  const canDelete = role === "owner";
                  const menuOpen = openMenuId === String(p._id);
                  return (
                    <div
                      key={p._id}
                      onClick={() => onChangeProject?.(String(p._id))}
                      className={`pim-conv-item ${currentProjectId === String(p._id) ? "pim-conv-item-active" : ""}`}
                      role="button"
                      tabIndex={0}
                    >
                      <svg className="w-[18px] h-[18px] text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                      </svg>
                      <span className="truncate flex-1">{p.name}</span>

                      {/* Menu "..." — chỉ hiện khi có ít nhất 1 hành động */}
                      {(canEdit || canShare || canDelete) && (
                        <div
                          className="relative flex-shrink-0"
                          ref={menuOpen ? projectMenuRef : null}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(menuOpen ? null : String(p._id));
                            }}
                            className="pim-conv-delete"
                            title="Tùy chọn dự án"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01" />
                            </svg>
                          </button>

                          {menuOpen && (
                            <div
                              className="absolute right-0 top-7 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1"
                              style={{ animation: "pimSlideUp 0.12s ease-out" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canEdit && (
                                <button
                                  type="button"
                                  onClick={() => openEditProject(p)}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Chỉnh sửa
                                </button>
                              )}
                              {canShare && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    setShareProjectItem(p);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                  </svg>
                                  Chia sẻ
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    setOpenMenuId(null);
                                    requestDeleteProject(p, e);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Xoá dự án
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {projects.length > PROJECTS_PREVIEW && (
                  <button
                    type="button"
                    onClick={() => setShowAllProjects((v) => !v)}
                    className="pim-side-row !text-gray-500"
                  >
                    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 12h.01M12 12h.01M19 12h.01" />
                    </svg>
                    {showAllProjects ? "Thu gọn" : "Thêm"}
                  </button>
                )}
              </>
            )}

            {/* ── Gần đây (đóng/mở toàn bộ list) ── */}
            <button
              type="button"
              onClick={() => setRecentCollapsed((v) => !v)}
              className="pim-side-label mt-3 w-full flex items-center justify-between gap-2 hover:bg-[#ececec] rounded-lg transition-colors"
              aria-expanded={!recentCollapsed}
            >
              <span>Gần đây</span>
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${recentCollapsed ? "-rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {!recentCollapsed && (
              <>
                {pinnedConvs.map((conv) => (
                  <ConvItem
                    key={conv.id}
                    conv={conv}
                    active={activeConversation === conv.id}
                    onSwitch={handleSwitch}
                    onRequestDelete={requestDeleteConv}
                    onTogglePin={onTogglePinned}
                  />
                ))}

                {otherConvs.length === 0 && pinnedConvs.length === 0 ? (
                  <p className="px-2 py-3 text-sm text-gray-400 italic">
                    Chưa có cuộc trò chuyện nào.
                  </p>
                ) : (
                  otherConvs.map((conv) => (
                    <ConvItem
                      key={conv.id}
                      conv={conv}
                      active={activeConversation === conv.id}
                      onSwitch={handleSwitch}
                      onRequestDelete={requestDeleteConv}
                      onTogglePin={onTogglePinned}
                    />
                  ))
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Footer — user profile */}
      <div className="border-t border-[#ececec] px-3 py-2.5">
        <div className="flex items-center gap-3 px-1 py-1.5 rounded-lg hover:bg-[#ececec] transition-colors cursor-pointer">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center text-white text-[13px] font-semibold flex-shrink-0">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">
              {displayName}
            </div>
            {email && (
              <div className="text-[12px] text-gray-500 truncate">
                {email}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal tạo dự án mới */}
      <CreateProjectModal
        open={createProjectOpen}
        loading={creatingProject}
        onCreate={handleCreateProjectSubmit}
        onClose={() => !creatingProject && setCreateProjectOpen(false)}
      />

      {/* Modal sửa dự án */}
      <EditProjectModal
        open={!!editProject}
        loading={editingProject}
        project={editProject}
        onSave={handleSaveProject}
        onClose={() => !editingProject && setEditProject(null)}
      />

      {/* Modal chia sẻ dự án */}
      <ShareProjectModal
        open={!!shareProjectItem}
        project={shareProjectItem}
        onClose={() => setShareProjectItem(null)}
      />

      {/* Confirm delete chat */}
      <ConfirmDialog
        open={!!confirmDeleteConv}
        title="Xóa cuộc trò chuyện?"
        message={
          confirmDeleteConv
            ? `Xóa "${confirmDeleteConv.title || "Chat"}"? Toàn bộ tin nhắn sẽ bị xoá và không thể khôi phục.`
            : ""
        }
        confirmText="Xóa"
        cancelText="Hủy"
        danger
        loading={deletingConv}
        onConfirm={handleConfirmDeleteConv}
        onCancel={() => !deletingConv && setConfirmDeleteConv(null)}
      />

      {/* Confirm delete project */}
      <ConfirmDialog
        open={!!confirmDeleteProject}
        title="Xóa dự án?"
        message={
          confirmDeleteProject
            ? `Xóa dự án "${confirmDeleteProject.name}"? Các chat bên trong sẽ chuyển sang "Chưa phân loại".`
            : ""
        }
        confirmText="Xóa dự án"
        cancelText="Hủy"
        danger
        loading={deletingProject}
        onConfirm={handleConfirmDeleteProject}
        onCancel={() => !deletingProject && setConfirmDeleteProject(null)}
      />
    </aside>
  );
}

function ConvItem({ conv, active, onSwitch, onRequestDelete, onTogglePin }) {
  return (
    <div
      onClick={() => onSwitch(conv.id)}
      className={`pim-conv-item mt-[2px] ${active ? "pim-conv-item-active" : ""}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSwitch(conv.id);
        }
      }}
    >
      <span className="truncate flex-1">{conv.title}</span>

      {/* Pin toggle — luôn hiện khi đã ghim, hover mới hiện khi chưa ghim */}
      {onTogglePin && (
        <button
          type="button"
          title={conv.pinned ? "Bỏ ghim" : "Ghim đoạn chat"}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin(conv.id);
          }}
          className={
            conv.pinned
              ? "flex-shrink-0 p-1 rounded-md text-gray-700"
              : "pim-conv-delete"
          }
          aria-label={conv.pinned ? "Bỏ ghim" : "Ghim đoạn chat"}
        >
          <svg className="w-4 h-4" fill={conv.pinned ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 4h6l-1 5 3 2v2H7v-2l3-2-1-5z M12 13v7" />
          </svg>
        </button>
      )}

      <button
        type="button"
        title="Xóa cuộc trò chuyện"
        onClick={(e) => {
          e.stopPropagation();
          onRequestDelete?.(conv);
        }}
        className="pim-conv-delete mb-[3px]"
        aria-label="Xóa cuộc trò chuyện"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>
  );
}

export default SidebarNavbar;
